// Phase 142.B — Searchable expectation picker for the Simplified
// form. Some grade+subject combos surface 40+ specific expectations,
// and a plain shadcn Select forces teachers to scroll through every
// one. This Combobox lets them type a code ("B1.1") or a few words
// from the description ("decompose numbers") and jump straight to it.
//
// Single-select on purpose — matches the Simplified form's UX of
// "pick one anchor expectation." The Full generator has its own
// multi-subject/multi-expectation picker and is NOT touched here.
import { useMemo, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const TRIGGER_BASE =
  "flex h-11 w-full items-center justify-between gap-2 rounded-md border border-[#4a6984]/20 bg-[#f4f5f7] px-3 py-2 text-left text-sm font-medium text-[#4a6984] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#96ded1] disabled:cursor-not-allowed disabled:opacity-50";

export default function ExpectationCombobox({
  options,
  value,
  onChange,
  disabled,
  placeholder = "Pick an Ontario expectation (optional)",
  testid = "fl-expectation",
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => options.find((o) => o.code === value) || null,
    [options, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className={TRIGGER_BASE}
        >
          {selected ? (
            <span className="flex min-w-0 items-baseline gap-2">
              <span className="shrink-0 font-extrabold text-[#1c2d3d]">
                {selected.code}
              </span>
              <span className="truncate text-[#4a6984]">
                {selected.description}
              </span>
            </span>
          ) : (
            <span className="text-[#4a6984]/65">{placeholder}</span>
          )}
          <span className="flex items-center gap-1 shrink-0">
            {selected && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Clear selected expectation"
                data-testid={`${testid}-clear`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange("");
                  }
                }}
                className="rounded-sm p-0.5 text-[#4a6984]/70 hover:text-[#1c2d3d] hover:bg-white cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#96ded1]"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-[#4a6984]/60" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0 bg-white border-[#4a6984]/15"
        data-testid={`${testid}-popover`}
      >
        <Command
          // Filter on BOTH the code and the description so a teacher
          // can type "B1.1" or "decompose" and find the same row.
          filter={(itemValue, search) => {
            if (!search) return 1;
            return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder="Search by code or keyword…"
            data-testid={`${testid}-search`}
            className="text-sm"
          />
          <CommandList className="max-h-72">
            <CommandEmpty>No expectations match that search.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const isSelected = opt.code === value;
                // value used by cmdk for filtering — include both code
                // AND description so the input matches either.
                const searchValue = `${opt.code} ${opt.description}`;
                return (
                  <CommandItem
                    key={opt.code}
                    value={searchValue}
                    data-testid={`${testid}-option-${opt.code}`}
                    onSelect={() => {
                      onChange(opt.code);
                      setOpen(false);
                    }}
                    className="items-start gap-2 py-2.5"
                  >
                    <Check
                      className={`mt-0.5 h-4 w-4 shrink-0 ${
                        isSelected ? "text-[#3EB489] opacity-100" : "opacity-0"
                      }`}
                    />
                    <span className="min-w-0">
                      <span className="block">
                        <span className="font-extrabold text-[#1c2d3d]">
                          {opt.code}
                        </span>
                      </span>
                      <span className="block text-xs text-[#4a6984] leading-snug mt-0.5">
                        {opt.description}
                      </span>
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
